import React from 'react';
import { useSelector } from 'react-redux';
import { ArrayHelpers, Forms, Loading, StringHelpers, SystemText } from 'e4shared';
import Filters from '../_filters';
import CompetencyPicker from 'components/admin/forms/competencyPicker';
import GroupPicker from 'components/admin/forms/groupPicker';
import MetricPicker from 'components/admin/forms/metricPicker';
import PeriodPicker from 'components/admin/forms/periodPicker';
import Board from './board';
import WidgetBase, { groupSet, metricFormatFromId, metricSet, widgetLoad, widgetLoadRecommendations } from '../_base';
import CompetencyRecommendations from '../components/competencyRecommendations';
import { sortGoalsByPeriod } from 'helpers/goals';
import GoalPicker from 'components/admin/forms/goalPicker';

export const Leaderboard = ({ defaultProps, id, item, setItem, onDelete }) => {
    const showFilters = useSelector(state => state.ui.filterWidget);
    const user = useSelector(state => state.user.user);
    const goals = useSelector(state => state.dashboard.goals);
    const mappedGoals = sortGoalsByPeriod(goals, true);
    const metrics = useSelector(state => state.dashboard.metrics);
    const [values, setValues] = React.useState({});
    const [errors, setErrors] = React.useState({});
    const [notConfigured, setNotConfigured] = React.useState(false);
    const [initialised, setInitialised] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [recLoading, setRecLoading] = React.useState(true);
    const [data, setData] = React.useState([]);
    const [recommendationData, setRecommendationData] = React.useState({});
    const [periodOptions, setPeriodOptions] = React.useState([]);

    const defaults = { show_For: true, period: 'M2', type1: 'C', groupBy: 'P' };

    React.useEffect(() => {
        if (item) {
            const parsed = JSON.parse(item.data);
            setValues({ ...defaults, ...parsed });
            setInitialised(true);
        }
    }, [item]);

    React.useEffect(() => {
        initialised && load();
    }, [initialised]);

    const load = () => {
        widgetLoad(item.id, user.guid, validator, setLoading, setNotConfigured, setData, () => {
            widgetLoadRecommendations(item.id, user.guid, setRecLoading, setRecommendationData);
        });
    };

    const updatePeriodOptions = () => {
        if (values?.goal1) {
            let goal = goals.find(g => g.id === values.goal1);

            const options = goal?.period ? [goal?.period] : null;
            if (!options?.some(o => values.period.charAt(0) === o)) {
                updateProperty(`${goal?.period}1`, 'period');
            }

            setPeriodOptions(options);
        }
    };

    React.useEffect(() => {
        updatePeriodOptions();
    }, [goals, values?.goal1, values?.period]);

    const updateProperty = (value, prop) => {
        const temp = { ...values };
        temp[prop] = value;

        if (prop === 'type1') {
            temp.metric1 = value === 'M' ? temp.metric1 : null;
            temp.competency1 = value === 'C' ? temp.competency1 : null;
            temp.goal1 = value === 'G' ? temp.goal1 : null;
        }

        setValues(temp);
    };

    const height = defaultProps.height(item);

    const groupByData = ['B', 'D', 'L', 'P', 'M']
        .map(x => ({ id: x, name: SystemText(`Dashboard/Widgets/General/Fields/GroupDataBy/Types/${x}`) }))
        .sort((a, b) => (a > b ? 1 : -1));

    const validator = suppressErrors => {
        const form = values || JSON.parse(item.data);

        const errors = {};
        let validForm = true;

        if (!form.name) {
            errors.name = SystemText('Dashboard/Widgets/General/Fields/Name/Error');
            validForm = false;
        }

        if (!form.groupBy) {
            errors.groupBy = SystemText('Dashboard/Widgets/General/Fields/GroupDataBy/Error');
            validForm = false;
        }

        if (!groupSet(form.group1)) {
            errors.group1 = SystemText('Dashboard/Widgets/Leaderboard/Fields/Group/Error');
            validForm = false;
        }

        if (form.type1 === 'C' && !form.competency1?.areaId && form.competency1?.areaId !== -1) {
            errors.competency1 = SystemText('Dashboard/Widgets/Leaderboard/Fields/Competency/Error');
            validForm = false;
        }

        if (form.type1 === 'M' && !metricSet(form.metric1)) {
            errors.metric1 = SystemText('Dashboard/Widgets/Leaderboard/Fields/Metric/Error');
            validForm = false;
        }

        if (form.type1 === 'G' && !form.goal1) {
            errors.goal1 = SystemText('Dashboard/Widgets/Leaderboard/Fields/Goal/Error');
            validForm = false;
        }

        if (
            form.type1 === 'M' &&
            form.metric1?.subMetricId &&
            (!form.metric1?.subMetricValues || form.metric1?.subMetricValues.length === 0)
        ) {
            errors.metric1 = SystemText('Dashboard/Widgets/Leaderboard/Fields/SubMetric/Error');
            validForm = false;
        }

        if (form.type1 !== 'C' && !form.period) {
            errors.period = SystemText('Dashboard/Widgets/Leaderboard/Fields/Period/Error');
            validForm = false;
        }

        if (!suppressErrors) setErrors(errors);
        return validForm;
    };

    const chart = (
        <div className="chart-container" id={`chart-${item.id}`} style={{ height: `${height}px` }}>
            <div className="chart" style={{ height: `${height}px` }}>
                <Board data={data} item={item} id={`chart-${item.id}`} />
            </div>
        </div>
    );

    const recommendations = () => {
        if (recLoading) return <Loading />;

        let result = [];

        const data = JSON.parse(item.data);

        const slug =
            data.metric1?.metricId || data.goal1
                ? 'Metric'
                : data.competency1?.areaId < 0
                ? 'All'
                : data.competency1?.id === 0
                ? 'Area'
                : 'Comp';

        const summary = SystemText(`Dashboard/Widgets/Leaderboard/Recommends/${slug}`);
        const none = SystemText(`Dashboard/Widgets/Leaderboard/Recommends/${slug}/None`);

        if (ArrayHelpers.existsAndHasLength(recommendationData?.best3)) {
            result.push(<CompetencyRecommendations text={summary} comps={recommendationData?.best3} showPlusMinus />);
        }

        if (recommendationData?.comps) {
            const metricFormat = data?.metric1 ? metricFormatFromId(data.metric1, metrics) : 'Q';
            result.push(
                <CompetencyRecommendations
                    text={SystemText(
                        'Dashboard/Widgets/MetricOverTime/Recommends/Strongest',
                        recommendationData?.best,
                        StringHelpers.formatMetric(metricFormat, recommendationData?.score, user.currencySymbol)
                    )}
                    comps={recommendationData?.comps}
                />
            );
        }

        if (!result.length) result.push(<div>{none}</div>);

        return result;
    };

    const filters = values ? (
        <Filters
            validator={validator}
            widget={item}
            values={values}
            errors={errors}
            onChange={(value, prop) => updateProperty(value, prop)}
            refresh={x => {
                setItem(x);
                load();
            }}
            show={showFilters === id}
            onDelete={onDelete}
        >
            <Forms.Select
                label={SystemText('Dashboard/Widgets/Leaderboard/Fields/GroupDataBy/Label')}
                help={SystemText('Dashboard/Widgets/Leaderboard/Fields/GroupDataBy/Help')}
                mandatory
                value={values.groupBy}
                error={errors.groupBy}
                data={groupByData}
                onChange={value => updateProperty(value, 'groupBy')}
            />
            <GroupPicker
                mandatory
                allowMultiple
                value={values.group1}
                error={errors.group1}
                onChange={value => updateProperty(value, 'group1')}
                showUserOption={defaultProps.company}
                controlId={id}
            />
            <Forms.Buttons label={SystemText('Dashboard/Widgets/Leaderboard/Fields/Type/Label')}>
                <button
                    className={`btn ${values.type1 === 'C' ? 'btn-primary' : ''}`}
                    onClick={() => updateProperty('C', 'type1')}
                >
                    {SystemText('Dashboard/Widgets/Leaderboard/Fields/Type/Ratings')}
                </button>
                <button
                    className={`btn ${values.type1 === 'M' ? 'btn-primary' : ''}`}
                    onClick={() => updateProperty('M', 'type1')}
                >
                    {SystemText('Dashboard/Widgets/Leaderboard/Fields/Type/Metrics')}
                </button>
                <button
                    className={`btn ${values.type1 === 'G' ? 'btn-primary' : ''}`}
                    onClick={() => updateProperty('G', 'type1')}
                >
                    {SystemText('Dashboard/Widgets/Leaderboard/Fields/Type/Goals')}
                </button>
            </Forms.Buttons>
            {values.type1 === 'C' && (
                <CompetencyPicker
                    label={SystemText('Dashboard/Widgets/Leaderboard/Fields/Competency/Label')}
                    mandatory
                    value={values.competency1}
                    error={errors.competency1}
                    onChange={value => {
                        updateProperty(value, 'competency1');
                    }}
                />
            )}
            {values.type1 === 'G' && (
                <>
                    <GoalPicker
                        label={SystemText('Dashboard/Widgets/Leaderboard/Fields/Goal/Label')}
                        mandatory
                        value={values.goal1}
                        error={errors.goal1}
                        data={mappedGoals}
                        onChange={value => {
                            updateProperty(value, 'goal1');
                        }}
                    />
                    <PeriodPicker
                        mandatory
                        limitTypes={periodOptions}
                        value={values.period}
                        error={errors.period}
                        onChange={value => updateProperty(value, 'period')}
                        help={SystemText('Dashboard/Widgets/Leaderboard/Fields/Period/Help')}
                    />
                </>
            )}
            {values.type1 === 'M' && (
                <div>
                    <MetricPicker
                        label={SystemText('Dashboard/Widgets/Leaderboard/Fields/Metric/Label')}
                        mandatory
                        allowRealtime
                        value={values.metric1}
                        error={errors.metric1}
                        onChange={value => {
                            updateProperty(value, 'metric1');
                        }}
                    />
                    {values.metric1?.metricRef?.toString().indexOf('RT-') < 0 && (
                        <PeriodPicker
                            mandatory
                            value={values.period}
                            error={errors.period}
                            metric={values.metric1?.type}
                            onChange={value => updateProperty(value, 'period')}
                            help={SystemText('Dashboard/Widgets/Leaderboard/Fields/Period/Help')}
                        />
                    )}
                </div>
            )}
        </Filters>
    ) : null;

    return (
        <WidgetBase
            filters={filters}
            chart={chart}
            height={height}
            item={item}
            recommendations={recommendations()}
            id={id}
            help={null}
            hideConfig={defaultProps.company && !user.administrator}
            extra={item.extra}
            loading={loading}
            notConfigured={notConfigured}
        />
    );
};

export default Leaderboard;
